import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/33eaf92f-0e3b-42a9-ab4a-ef51b9bcdb58.png"
              alt="KANTUNAL d. o. o. Logo"
              className="h-10 w-10 object-contain"
            />
            <span
              className={`text-xl font-semibold transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              KANTUNAL d. o. o.
            </span>
          </div>
          <div className="flex gap-8">
            {['home', 'about', 'products', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`text-sm font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? 'text-gray-700 hover:text-purple-600'
                    : 'text-white hover:text-purple-300'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h1 className="text-6xl lg:text-7xl font-bold text-white leading-tight">
                Crafting Premium
                <br />
                iOS Applications
                <br />
                For Modern Users
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed max-w-xl">
                A team of experts developing modern applications for the iOS platform. Our products enhance user experience and improve mobile device security.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/47a7e81d5753208f68271e36b65cf87a.jpg"
                  alt="VPN Application Interface"
                  className="w-80 h-auto object-contain drop-shadow-2xl transform rotate-6 hover:rotate-0 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
            <div className="space-y-6">
              <span className="text-sm font-bold text-orange-500 tracking-widest uppercase">
                Who We Are
              </span>
              <h2 className="text-5xl font-bold text-gray-900 leading-tight">
                Expert Team Building
                <br />
                Secure Mobile Solutions
              </h2>
            </div>
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-blue-500 transform hover:scale-105 transition-transform duration-300">
                <p className="text-gray-700 leading-relaxed">
                  Our team consists of highly skilled iOS developers, security experts, and UX designers who are passionate about creating applications that make a real difference in users' daily lives.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-purple-500 transform hover:scale-105 transition-transform duration-300 lg:ml-8">
                <p className="text-gray-700 leading-relaxed">
                  We specialize in developing applications that prioritize user privacy and device performance. Every line of code is crafted with attention to detail and security best practices.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-orange-500 transform hover:scale-105 transition-transform duration-300 lg:ml-16">
                <p className="text-gray-700 leading-relaxed">
                  Our commitment to excellence has earned us recognition in the iOS development community. We continuously innovate to stay ahead of industry trends and user expectations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-20">
            Our Applications
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* VPN Card */}
            <div
              className="rounded-3xl p-12 shadow-xl transform hover:scale-105 transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #06b6d4 100%)',
              }}
            >
              <div className="flex justify-center mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=3D%20isometric%20shield%20icon%20illustration%2C%20white%20and%20blue%20colors%2C%20security%20protection%20symbol%2C%20modern%20minimalist%20design%2C%20floating%20on%20gradient%20background%2C%20professional%20tech%20illustration%2C%20clean%20geometric%20shapes%2C%20premium%20quality%20render&width=300&height=300&seq=vpn-icon-001&orientation=squarish"
                  alt="VPN Security Shield"
                  className="w-48 h-48 object-contain"
                />
              </div>
              <h3 className="text-3xl font-bold text-white mb-6 text-center">
                VPN Application
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-white">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-lg leading-relaxed">
                    Military-grade encryption to protect your online privacy and secure your data
                  </span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-lg leading-relaxed">
                    Lightning-fast connection speeds with servers in over 50 countries worldwide
                  </span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-lg leading-relaxed">
                    User-friendly interface with one-tap connection and automatic server selection
                  </span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-lg leading-relaxed">
                    No-logs policy ensuring complete anonymity and privacy protection
                  </span>
                </li>
              </ul>
            </div>

            {/* Cleaner Card */}
            <div
              className="rounded-3xl p-12 shadow-xl transform hover:scale-105 transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #fda4af 0%, #fb923c 100%)',
              }}
            >
              <div className="flex justify-center mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=3D%20isometric%20broom%20cleaning%20icon%20illustration%2C%20white%20and%20coral%20pink%20colors%2C%20optimization%20symbol%2C%20modern%20minimalist%20design%2C%20floating%20on%20gradient%20background%2C%20professional%20tech%20illustration%2C%20clean%20geometric%20shapes%2C%20premium%20quality%20render&width=300&height=300&seq=cleaner-icon-001&orientation=squarish"
                  alt="Cleaner Optimization Tool"
                  className="w-48 h-48 object-contain"
                />
              </div>
              <h3 className="text-3xl font-bold text-white mb-6 text-center">
                Cleaner Application
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-white">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-lg leading-relaxed">
                    Intelligent storage analysis to identify and remove unnecessary files and cache
                  </span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-lg leading-relaxed">
                    Photo and video management with duplicate detection and compression tools
                  </span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-lg leading-relaxed">
                    Battery optimization features to extend device life and improve performance
                  </span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-lg leading-relaxed">
                    Safe and secure cleaning process that never touches important system files
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-900 rounded-t-[3rem]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            <div className="lg:col-span-3 space-y-8">
              <h2 className="text-4xl font-bold text-white mb-12">Get In Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-gray-400 text-sm">Address</span>
                    <p className="text-white text-lg mt-1">Garica 136</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-2-line text-xl text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-gray-400 text-sm">City</span>
                    <p className="text-white text-lg mt-1">Garica</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-gray-400 text-sm">Zip</span>
                    <p className="text-white text-lg mt-1">51516</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-global-line text-xl text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-gray-400 text-sm">Country</span>
                    <p className="text-white text-lg mt-1">Croatia</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mail-send-line text-xl text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-gray-400 text-sm">Email</span>
                    <p className="text-white text-lg mt-1">help@kantunaldoo.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-phone-line text-xl text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <span className="text-gray-400 text-sm">Number</span>
                    <p className="text-white text-lg mt-1">+1 5673473437</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-8">
              <p className="text-gray-300 text-lg leading-relaxed">
                At KANTUNAL d. o. o., we are committed to protecting user privacy and delivering exceptional mobile experiences. Our applications are built with the highest standards of security and performance in mind.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                We believe in transparency, innovation, and putting our users first. Every feature we develop is designed to solve real problems and enhance the way people use their iOS devices.
              </p>
              <p className="text-white text-sm mt-12">KANTUNAL d. o. o.</p>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm text-center">
                © 2025 KANTUNAL d. o. o. All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 text-sm hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
